/**
 * Group-by selector + grouping helper that produces a flat list of
 * GroupHeader / Row entries the virtualiser can scroll over.
 */
import type { Card } from '@chaos/types/kanban';

import { cn } from '../../../lib/utils';
import type { CardListGrouping } from './list-types';

export interface CardGroup {
  key: string;
  label: string;
  cards: Card[];
}

const OPTIONS: ReadonlyArray<{
  value: CardListGrouping['by'];
  label: string;
}> = [
  { value: null, label: 'No grouping' },
  { value: 'autonomy', label: 'Autonomy' },
  { value: 'agent', label: 'Agent' },
  { value: 'due', label: 'Due date' },
];

export function ListGrouping({
  value,
  onChange,
}: {
  value: CardListGrouping;
  onChange: (next: CardListGrouping) => void;
}) {
  return (
    <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
      Group:
      <select
        value={value.by ?? ''}
        onChange={(e) => {
          const v = e.target.value || null;
          onChange({ by: v as CardListGrouping['by'] });
        }}
        className={cn(
          'h-7 rounded border border-border bg-background px-1 text-xs text-foreground',
          'focus:border-foreground/30 focus:outline-none',
        )}
      >
        {OPTIONS.map((o) => (
          <option key={String(o.value)} value={o.value ?? ''}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function groupCards(cards: readonly Card[], grouping: CardListGrouping): CardGroup[] {
  if (grouping.by === null) {
    return [{ key: '__all', label: 'All cards', cards: [...cards] }];
  }
  const buckets = new Map<string, CardGroup>();
  for (const c of cards) {
    const { key, label } = groupingKey(c, grouping.by);
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = { key, label, cards: [] };
      buckets.set(key, bucket);
    }
    bucket.cards.push(c);
  }
  return Array.from(buckets.values()).sort((a, b) => a.label.localeCompare(b.label));
}

function groupingKey(
  card: Card,
  by: NonNullable<CardListGrouping['by']>,
): {
  key: string;
  label: string;
} {
  switch (by) {
    case 'autonomy':
      return {
        key: card.autonomyLevel ?? 'none',
        label: card.autonomyLevel ?? 'No autonomy set',
      };
    case 'agent':
      return {
        key: card.primaryAgentId ?? 'none',
        label: card.primaryAgentId ? card.primaryAgentId.slice(0, 8) : 'Unassigned',
      };
    case 'due': {
      if (!card.dueDate) return { key: 'no-due', label: 'No due date' };
      const d = new Date(card.dueDate);
      const yyyymm = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
      return { key: yyyymm, label: yyyymm };
    }
    case 'status':
      return {
        key: card.archivedAt ? 'archived' : 'active',
        label: card.archivedAt ? 'Archived' : 'Active',
      };
    case 'priority':
      return { key: 'normal', label: 'Normal' };
    case 'title':
      return {
        key: card.title.charAt(0).toUpperCase(),
        label: card.title.charAt(0).toUpperCase(),
      };
    case 'department':
      return { key: 'workspace', label: 'Workspace' };
    case 'project':
      return { key: card.boardId, label: card.boardId.slice(0, 8) };
    default:
      return { key: '__other', label: 'Other' };
  }
}
